<template>
    <div class="w-full space-y-3">
        <Head title="Reports"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl">My Reports</h1>
        </div>
    </div>
    <br />
    <div class="mt-5 bg-white py-6 px-4 sm:p-6 shadow mb-6">
        <!-- Finance Reports -->
        <h2 class="font-bold mb-6 text-xl">Finance Reports</h2>
        <div class="divide-y border-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0">
            <div v-for="(financeReport, financeReportIdx) in financeReports" :key="financeReport.title" :class="[financeReportIdx === 0 ? 'rounded-tl-lg rounded-tr-lg sm:rounded-tr-none' : '', financeReportIdx === 1 ? 'sm:rounded-tr-lg' : '', financeReportIdx === financeReport.length - 2 ? 'sm:rounded-bl-lg' : '', financeReportIdx === financeReports.length - 1 ? 'rounded-bl-lg rounded-br-lg sm:rounded-bl-none' : '', 'relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500']">
                <div>
                    <span :class="[financeReport.iconBackground, financeReport.iconForeground, 'rounded-lg inline-flex p-3 ring-4 ring-white']">
                    <component :is="financeReport.icon" class="h-6 w-6" aria-hidden="true" />
                    </span>
                </div>
                <div class="mt-8">
                    <h3 class="text-lg font-medium">
                        <a :href="financeReport.href" class="focus:outline-none">
                            <span class="absolute inset-0" aria-hidden="true" />
                            {{ financeReport.title }}
                        </a>
                    </h3>
                </div>
                <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400" aria-hidden="true">
                    <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                    </svg>
                </span>
            </div>
        </div>
        <br />
        <br />
        <h2 class="font-bold mb-6 text-xl">Operational Reports</h2>
        <div class="divide-y border-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0">
            <div v-for="(operationalReport, operationalReportIdx) in operationalReports" :key="operationalReport.title" :class="[operationalReportIdx === 0 ? 'rounded-tl-lg rounded-tr-lg sm:rounded-tr-none' : '', operationalReportIdx === 1 ? 'sm:rounded-tr-lg' : '', operationalReportIdx === operationalReport.length - 2 ? 'sm:rounded-bl-lg' : '', operationalReportIdx === operationalReports.length - 1 ? 'rounded-bl-lg rounded-br-lg sm:rounded-bl-none' : '', 'relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500']">
                <div>
                    <span :class="[operationalReport.iconBackground, operationalReport.iconForeground, 'rounded-lg inline-flex p-3 ring-4 ring-white']">
                    <component :is="operationalReport.icon" class="h-6 w-6" aria-hidden="true" />
                    </span>
                </div>
                <div class="mt-8">
                    <h3 class="text-lg font-medium">
                        <a :href="operationalReport.href" class="focus:outline-none">
                            <span class="absolute inset-0" aria-hidden="true" />
                            {{ operationalReport.title }}
                        </a>
                    </h3>
                </div>
                <span class="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400" aria-hidden="true">
                    <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                    </svg>
                </span>
            </div>
        </div>
        <br />
    </div>
</template>

<script>
    import Authenticated from "@/Layouts/Authenticated";
    import {Link, Head} from "@inertiajs/vue3";
    import {CalendarIcon, CashIcon, ChartBarIcon, UserGroupIcon, UsersIcon} from '@heroicons/vue/outline'

    export default {
        layout: Authenticated,
        components: {
            Head,
            Link,
        },
        data() {
            return {
                financeReports: financeReports,
                operationalReports: operationalReports
            }
        }
    }

    const financeReports = [
        {
            title: 'Invoice Report',
            href: '/reports/finance-reports/invoice-report',
            icon: CalendarIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
        },
        {
            title: 'Commission Invoice Report',
            href: '/reports/finance-reports/commission-invoice-report',
            icon: CashIcon,
            iconForeground: 'text-lime-700',
            iconBackground: 'bg-lime-50',
        },
        {
            title: 'Invoice Status Report (Suppliers)',
            href: '/reports/finance-reports/invoice-status-report-suppliers',
            icon: UserGroupIcon,
            iconForeground: 'text-purple-700',
            iconBackground: 'bg-purple-50',
        },
        {
            title: 'Invoice Status Report (Clients)',
            href: '/reports/finance-reports/invoice-status-report-clients',
            icon: CashIcon,
            iconForeground: 'text-sky-700',
            iconBackground: 'bg-sky-50',
        },
        {
            title: 'Client Details List',
            href: '/reports/finance-reports/client-details-list',
            icon: ChartBarIcon,
            iconForeground: 'text-yellow-700',
            iconBackground: 'bg-yellow-50',
        },
        {
            title: 'Expenses Report',
            href: '/reports/finance-reports/expenses-report',
            icon: ChartBarIcon,
            iconForeground: 'text-yellow-700',
            iconBackground: 'bg-yellow-50',
        },
        {
            title: 'Expenses Import Report',
            href: '/reports/finance-reports/expenses-import-report',
            icon: ChartBarIcon,
            iconForeground: 'text-yellow-700',
            iconBackground: 'bg-yellow-50',
        },
        {
            title: 'Sales Proforma Report',
            href: '/reports/finance-reports/sales-proforma-report',
            icon: ChartBarIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
        },
        {
            title: 'Supplier Revenue Report',
            href: '/reports/finance-reports/supplier-revenue-report',
            icon: ChartBarIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
        },
        {
            title: 'Commission Check Report',
            href: '/reports/finance-reports/commission-check-report',
            icon: CashIcon,
            iconForeground: 'text-green-700',
            iconBackground: 'bg-green-50',
        }
    ]

    const operationalReports = [
        {
            title: 'Client Quote Report',
            href: '/reports/operational-reports/client-quote-report',
            icon: CalendarIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
        },
        {
            title: 'Supplier Report',
            href: '/reports/operational-reports/supplier-report',
            icon: UserGroupIcon,
            iconForeground: 'text-purple-700',
            iconBackground: 'bg-purple-50',
        },
        {
            title: 'Month End Report',
            href: '/reports/operational-reports/month-end-report',
            icon: CalendarIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
        },
        // {
        //     title: 'Fixture Overall Report',
        //     href: '/reports/operational-reports/fixture-overall-report',
        //     icon: CalendarIcon,
        //     iconForeground: 'text-teal-700',
        //     iconBackground: 'bg-teal-50',
        // },
        {
            title: 'Performance Overview',
            href: '/reports/operational-reports/performance-overview',
            icon: UserGroupIcon,
            iconForeground: 'text-purple-700',
            iconBackground: 'bg-purple-50',
        },
        // {
        //     title: 'Unit 4 Gap Report',
        //     href: '/reports/operational-reports/unit-4-gap-report',
        //     icon: CashIcon,
        //     iconForeground: 'text-sky-700',
        //     iconBackground: 'bg-sky-50',
        // },
        // {
        //     title: 'Monthly Management Fee Report',
        //     href: '/reports/operational-reports/monthly-management-fee-report',
        //     icon: ChartBarIcon,
        //     iconForeground: 'text-yellow-700',
        //     iconBackground: 'bg-yellow-50',
        // },
        // {
        //     title: 'Supplier Spend Report',
        //     href: '/reports/operational-reports/supplier-spend-report',
        //     icon: ChartBarIcon,
        //     iconForeground: 'text-yellow-700',
        //     iconBackground: 'bg-yellow-50',
        // },
        // {
        //     title: 'Corrections Report',
        //     href: '/reports/operational-reports/corrections-report',
        //     icon: ChartBarIcon,
        //     iconForeground: 'text-yellow-700',
        //     iconBackground: 'bg-yellow-50',
        // },
    ]
</script>
